import * as React from 'react';
import { CaseReportView } from 'app/area/case-report/case-report-view';
import { STATE } from 'app/constants';
import { useTranslation } from 'app/infrastructure/translate/hooks';
import { caseListingTranslation } from 'app/area/case-report/case-listing/constants';
import { useSelectors } from 'app/area/case-report/case-listing/hooks';
import { HeaderVisibilityConfiguration } from 'app/components';
import { FavouriteDefinition, RenderMode } from 'app/types-shared';
import { PrimaryActionComponentFactory } from 'app/area/case-report/case-listing/types';
import { usePresentationType } from 'app/area/favourite/hooks';
import {presentationType} from './constants';
import { useAutoExecuteState } from 'app/area/case-report/hooks';
import { BowtieListing } from 'app/area/case-report/bowtie-listing/bowtie-listing';
import { CaseListing } from 'app/area/case-report/case-listing/case-listing';
import { CaseReportFailedExecute } from 'app/area/case-report/components/case-report-failed-execute';

interface CaseReportProps {
  workingDefinition: FavouriteDefinition;
  report?: number;
}

export interface CaseReportExternalProps {
  headerVisibilityConfiguration?: HeaderVisibilityConfiguration;
  windowAsScrollableAncestor?: boolean;
  requestedRenderMode?: RenderMode;
  hideLoadResults?: boolean;
  hideNumberOfHits?: boolean;
  primaryActionComponentFactory?: PrimaryActionComponentFactory;
}

export const CaseReport = ({
  report,
  workingDefinition,
  hideLoadResults,
  hideNumberOfHits,
  primaryActionComponentFactory,
  requestedRenderMode,
  windowAsScrollableAncestor,
  headerVisibilityConfiguration
}: CaseReportProps & CaseReportExternalProps) => {
  
  const {
    fetching,
    results,
    totalNumberOfResults,
    errorsExecuteFailed
  } = useSelectors();

  const {
    autoExecute,
    toggleAutoExecution
  } = useAutoExecuteState();

  const translation = useTranslation(STATE.caseListing, caseListingTranslation);
  const currentPresentationType = usePresentationType();

  let result = null;
  let classNames = '';

  const hasExecuteFailedErrors = autoExecute && !!errorsExecuteFailed && !!errorsExecuteFailed.length;

  const executeFailed = hasExecuteFailedErrors ? <CaseReportFailedExecute 
    translation={translation} 
    errorsExecuteFailed={errorsExecuteFailed} /> : null;

  switch(currentPresentationType) {
    case presentationType.bowtieListing:
      classNames = 'bowtielisting';
      result = 
        <BowtieListing
          translation={translation}
          autoExecute={autoExecute}
          results={results}
          totalNumberOfResults={totalNumberOfResults}
          fetching={fetching}
          workingDefinition={workingDefinition}
          executeFailed={executeFailed} />;
      break;

    default:
      classNames = 'caselisting narrow-screen';
      result = 
        <CaseListing
          translation={translation}
          autoExecute={autoExecute}
          results={results}
          totalNumberOfResults={totalNumberOfResults}
          fetching={fetching}
          workingDefinition={workingDefinition}
          requestedRenderMode={requestedRenderMode}
          primaryActionComponentFactory={primaryActionComponentFactory}
          windowAsScrollableAncestor={windowAsScrollableAncestor}          
          headerVisibilityConfiguration={headerVisibilityConfiguration}
          executeFailed={executeFailed} />;
      break;
  }
  
  return <CaseReportView
    classNames={classNames}
    hideLoadResults={hideLoadResults}
    hideNumberOfHits={hideNumberOfHits}
    totalNumberOfResults={totalNumberOfResults}
    translation={translation}
    autoExecute={autoExecute}
    toggleAutoExecution={toggleAutoExecution}
    report={report}
    fetching={fetching}>
    {result}
  </CaseReportView>;
};
